import { apiFetch } from "@/lib/jwt-auth-client";
import type { BrandIdentity, BrandConfig, BrandDirectorySummary } from "@/config/brand";

/** GET /brands/ — list all brand identities for the current user. */
export async function fetchBrandsList(
  signal?: AbortSignal,
): Promise<BrandDirectorySummary[]> {
  const res = await apiFetch("/brands/", { signal });
  if (!res.ok) throw new Error("Failed to fetch brands");
  return (await res.json()) as BrandDirectorySummary[];
}

/** GET /brands/active/ — return the currently active brand id (string, may be ""). */
export async function fetchActiveBrandId(signal?: AbortSignal): Promise<string> {
  const res = await apiFetch("/brands/active/", { signal });
  if (!res.ok) throw new Error("Failed to fetch active brand");
  const data = (await res.json()) as { activeBrandId?: string };
  return data.activeBrandId ?? "";
}

/** GET /brands/:id/ — full brand config. */
export async function fetchBrandConfig(
  id: string,
  signal?: AbortSignal,
): Promise<BrandConfig> {
  const res = await apiFetch(`/brands/${id}/`, { signal });
  if (!res.ok) throw new Error("Failed to load brand config");
  return (await res.json()) as BrandConfig;
}

/** PUT /brands/active/ — set active brand id. */
export async function setActiveBrandId(id: string): Promise<void> {
  const res = await apiFetch("/brands/active/", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ activeBrandId: id }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Failed to set active brand");
  }
}

/** PUT /brands/:id/ — replace brand config. */
export async function saveBrandConfig(brand: BrandConfig): Promise<void> {
  const res = await apiFetch(`/brands/${brand.identity.id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(brand),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Failed to save brand");
  }
}

/** POST /brands/ — create a new brand. Returns the new identity. */
export async function createBrandConfig(brand: BrandConfig): Promise<BrandIdentity> {
  const res = await apiFetch("/brands/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(brand),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Failed to create brand");
  }
  return (await res.json()) as BrandIdentity;
}

/** DELETE /brands/:id/ — remove a brand. */
export async function deleteBrandConfig(id: string): Promise<void> {
  const res = await apiFetch(`/brands/${id}/`, { method: "DELETE" });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Failed to delete brand");
  }
}
